import React, { useCallback, useEffect, useRef, useState } from "react"

/**
 * Tooltip components for displaying hover and click-triggered overlays.
 *
 * @see docs/ui/smart-tooltip-positioning.md for guidance on dynamically
 * calculating tooltip position to avoid clipping by overflow containers.
 */

export const PopUpPosition = {
  TopLeft: "topLeft",
  Top: "top",
  TopRight: "topRight",
  Bottom: "bottom",
  BottomLeft: "bottomLeft",
  BottomRight: "bottomRight",
}

/**
 * Default minimum space (in pixels) required above/below an element to show tooltip.
 * If less space is available, tooltip flips to opposite side.
 */
const DEFAULT_TOOLTIP_THRESHOLD = 100

/**
 * Calculates the optimal tooltip position based on element location relative to
 * a container. Flips the position vertically and/or horizontally when near edges.
 *
 * @param {HTMLElement} element - The trigger element
 * @param {HTMLElement|null} [container=null] - Container with overflow (defaults to viewport)
 * @param {number} [threshold=100] - Minimum space (px) required to show tooltip on a side
 * @param {string} initialPosition - Starting position from PopUpPosition, flipped when in danger zone
 * @returns {string} One of PopUpPosition values
 *
 * @example
 * // Tooltip flips to bottomRight when near top edge
 * const position = calculateTooltipPosition(el, container, 100, PopUpPosition.TopRight)
 *
 * @see docs/ui/smart-tooltip-positioning.md for detailed documentation
 */
export function calculateTooltipPosition(element, container = null, threshold = DEFAULT_TOOLTIP_THRESHOLD, initialPosition) {
  if (!element) return initialPosition

  const elementRect = element.getBoundingClientRect()

  // Use container bounds if provided, otherwise use viewport
  const bounds = container
    ? container.getBoundingClientRect()
    : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth }

  const spaceAbove = elementRect.top - bounds.top
  const spaceBelow = bounds.bottom - elementRect.bottom

  const nearTop = spaceAbove < threshold
  const nearBottom = spaceBelow < threshold

  let position = initialPosition

  // Flip vertically when in danger zone
  if (nearTop) {
    switch (position) {
      case PopUpPosition.Top: position = PopUpPosition.Bottom; break
      case PopUpPosition.TopLeft: position = PopUpPosition.BottomLeft; break
      case PopUpPosition.TopRight: position = PopUpPosition.BottomRight; break
    }
  } else if (nearBottom) {
    switch (position) {
      case PopUpPosition.Bottom: position = PopUpPosition.Top; break
      case PopUpPosition.BottomLeft: position = PopUpPosition.TopLeft; break
      case PopUpPosition.BottomRight: position = PopUpPosition.TopRight; break
    }
  }

  return position
}

/**
 * PopUp component for displaying tooltip-style overlays
 * @param {boolean} show - Controls visibility of the popup
 * @param {string} position - Position of the popup relative to its trigger (defaults to 'top')
 * @param {React.ReactNode} children - Content to display inside the popup
 * @param {string} className - Additional CSS classes to apply
 * @param {...any} props - Additional props passed to the root div element
 * @returns {JSX.Element} PopUp component
 */
export const PopUp = React.memo(
  React.forwardRef(function PopUp(
    { show, position = PopUpPosition.Top, children, className = "", ...props },
    ref
  ) {
    const positionClass = position ? ` ${position}` : ""
    const tooltipClassName = `tooltip${positionClass} ${className}`.trim()

    return (
      <div ref={ref} className={tooltipClassName} {...props}>
        <div className="tooltip-container" aria-hidden={!show}>
          <div className="tooltip-wrapper">{children}</div>
        </div>
      </div>
    )
  })
)

/**
 * HoverTooltip component that displays a tooltip on hover with configurable delay
 * @param {Object} props - Component properties
 * @param {string|JSX.Element} props.tooltip - Tooltip content to display
 * @param {boolean} [props.disabled=false] - Whether tooltip should be disabled (no hover effect)
 * @param {string} [props.position='top'] - Position of the tooltip (top, topLeft, topRight, bottom, bottomLeft, bottomRight)
 * @param {number} [props.delay=500] - Delay in milliseconds before showing tooltip on hover
 * @param {number} [props.hideDelay=150] - Delay in milliseconds before hiding tooltip on mouse leave
 * @param {function} [props.onClick] - Click handler for the tooltip wrapper (sets cursor: pointer when provided)
 * @param {React.ReactNode} props.children - Content to wrap with tooltip functionality
 * @returns {JSX.Element} Wrapper div with hover tooltip functionality
 */
export const HoverTooltip = React.memo(function HoverTooltip({
  tooltip,
  disabled = false,
  position = PopUpPosition.Top,
  delay = 500,
  hideDelay = 150,
  className = "",
  onClick,
  children,
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const showTimeoutRef = useRef(null)
  const hideTimeoutRef = useRef(null)
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).slice(2, 9)}`)

  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      window.clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (disabled || !tooltip) return

    clearTimeouts()
    showTimeoutRef.current = window.setTimeout(() => {
      setShowTooltip(() => true)
    }, delay)
  }, [disabled, tooltip, delay, clearTimeouts])

  const handleMouseLeave = useCallback(() => {
    clearTimeouts()
    hideTimeoutRef.current = window.setTimeout(() => {
      setShowTooltip(() => false)
    }, hideDelay)
  }, [clearTimeouts, hideDelay])

  useEffect(() => {
    return () => {
      clearTimeouts()
    }
  }, [clearTimeouts])

  return (
    <div
      className={`hover-tooltip ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      style={onClick ? { cursor: "pointer" } : undefined}
      aria-describedby={showTooltip ? tooltipId.current : undefined}
    >
      {children}
      {showTooltip && tooltip && !disabled && (
        <PopUp
          show={showTooltip}
          position={position}
          id={tooltipId.current}
          role="tooltip"
          aria-expanded={showTooltip}
        >
          {tooltip}
        </PopUp>
      )}
    </div>
  )
})

/**
 * InfoTooltip component that displays an info icon alongside content with click-to-show tooltip
 * @param {Object} props - Component properties
 * @param {string|JSX.Element} props.tooltip - Tooltip content to display
 * @param {boolean} [props.disabled=false] - Whether tooltip should be disabled (grayed icon, no click)
 * @param {string} [props.position='topLeft'] - Position of the tooltip
 * @param {React.ReactNode} props.children - Content to display alongside the info icon
 * @returns {JSX.Element} Wrapper div with info icon and click tooltip functionality
 */
export const InfoTooltip = React.memo(function InfoTooltip({
  tooltip,
  disabled = false,
  position = PopUpPosition.TopLeft,
  children,
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipId = useRef(`info-tooltip-${Math.random().toString(36).slice(2, 9)}`)
  const tooltipRef = useRef(null)
  const iconRef = useRef(null)
  const liveRegionId = useRef(`live-${Math.random().toString(36).slice(2, 9)}`)

  const handleIconClick = useCallback(() => {
    if (disabled || !tooltip) return
    setShowTooltip(prev => !prev)
  }, [disabled, tooltip])

  const handleDismiss = useCallback((restoreFocus = false) => {
    setShowTooltip(() => false)
    if (restoreFocus && iconRef.current) {
      iconRef.current.focus()
    }
  }, [])

  const handleIconKeyDown = useCallback(
    event => {
      if (disabled || !tooltip) return

      // Handle Enter and Space keys to toggle tooltip
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault() // Prevent page scroll on Space
        setShowTooltip(prev => !prev)
      }
    },
    [disabled, tooltip]
  )

  // Handle outside clicks
  useEffect(() => {
    if (!showTooltip) return

    const handleClickOutside = event => {
      if (
        tooltipRef.current &&
        iconRef.current &&
        !tooltipRef.current.contains(event.target) &&
        !iconRef.current.contains(event.target)
      ) {
        handleDismiss()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showTooltip, handleDismiss])

  // Handle ESC key
  useEffect(() => {
    if (!showTooltip) return

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        handleDismiss(true) // Restore focus when ESC is pressed
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [showTooltip, handleDismiss])

  return (
    <div className="info-tooltip">
      {children}
      <div
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          ref={iconRef}
          className={`info-icon ${disabled ? "disabled" : ""}`}
          onClick={handleIconClick}
          onKeyDown={handleIconKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Show more information"
          aria-expanded={showTooltip}
          aria-describedby={showTooltip ? tooltipId.current : liveRegionId.current}
          style={{
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            userSelect: "none",
            outline: "none", // Remove default outline, we'll use focus-visible
          }}
        >
          ⓘ
        </span>
        {showTooltip && tooltip && !disabled && (
          <PopUp
            ref={tooltipRef}
            show={showTooltip}
            position={position}
            id={tooltipId.current}
            role="tooltip"
            aria-expanded={showTooltip}
          >
            {tooltip}
          </PopUp>
        )}
      </div>
      {/* Screen reader live region for tooltip state announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          left: "-10000px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        {showTooltip ? "Tooltip opened" : ""}
      </div>
    </div>
  )
})
